import { clampRect, median, toPixelRect } from './geometry'
import { mapProcessedPolygonToFrameRect, type PreparedOcrImage } from './image'
import type {
  AdaptiveMetricRoi,
  AdaptiveRoiSource,
  RecognitionMetricKey,
  NormalizedRect,
} from '@/domain/types'
import type { OcrReading, OcrTextItem } from '@/services/ocr-engine'

export interface AdaptiveTextCandidate {
  readonly rect: NormalizedRect
  readonly key: string
  readonly confidence: number
  readonly capturedAt: number
}

export interface AdaptiveBarCandidate {
  readonly rect: NormalizedRect
  readonly confidence: number
  readonly capturedAt: number
}

interface MetricState {
  searchRect: NormalizedRect
  source: AdaptiveRoiSource
  textRect: NormalizedRect | null
  barRect: NormalizedRect | null
  textCandidates: AdaptiveTextCandidate[]
  barCandidates: AdaptiveBarCandidate[]
  legacyTextFailures: number
  legacyBarFailures: number
  adaptiveTextFailures: number
  adaptiveBarFailures: number
  failureStartedAt: number | null
  confidence: number
  updatedAt: number | null
  fallbackReason: 'legacy-success' | 'adaptive-failed' | 'conflict' | null
}

const TEXT_CONFIRMATION_WINDOW_MS = 6_000
const JOB_TEXT_CONFIRMATION_WINDOW_MS = 65_000
const TEXT_CONFIRMATION_IOU = 0.65
const BAR_EDGE_TOLERANCE = 0.02
const ADAPTED_FAILURE_LIMIT = 3
const LEGACY_FAILURE_LIMIT = 2

export interface SelectedAdaptiveText<T> {
  readonly value: T
  readonly text: string
  readonly fullText: string
  readonly confidence: number
  readonly rect: NormalizedRect
}

export function selectAdaptiveText<T>(
  metric: RecognitionMetricKey,
  reading: OcrReading,
  prepared: PreparedOcrImage,
  parser: (text: string) => T | null,
  searchRect: NormalizedRect,
): SelectedAdaptiveText<T> | null {
  const fullValue = parser(reading.text)
  let items = (reading.items ?? [])
    .map((item, index) => ({ item, index, rect: mapProcessedPolygonToFrameRect(prepared, item.polygon) }))
    .filter((entry): entry is { item: OcrTextItem; index: number; rect: NormalizedRect } => entry.rect !== null)
  if (metric === 'jobName') {
    const upperBoundary = searchRect.y + searchRect.height * 0.62
    items = items.filter(({ item }) => {
      // Use the original center: clipping a lower row to the crop can move its
      // center upward and incorrectly merge an adjacent level or nickname.
      const ys = item.polygon.map(([, y]) => y)
      const centerY = (Math.min(...ys) + Math.max(...ys)) / 2
      const frameCenterY = prepared.sourceRect.y
        + (centerY - prepared.transform.contentY) / Math.max(1, prepared.transform.contentHeight) * prepared.sourceRect.height
      return frameCenterY <= upperBoundary
    })
  }
  if (!items.length) return null
  if (metric === 'jobName') {
    const ordered = [...items].sort((left, right) => left.rect.x - right.rect.x)
    const runs: typeof items[] = []
    for (const entry of ordered) {
      const current = runs.at(-1)
      const previous = current?.at(-1)
      const sameLine = current && itemsShareLine([...current.map((item) => item.rect), entry.rect])
      const maximumHeight = previous ? Math.max(previous.rect.height, entry.rect.height) : 0
      const gap = previous ? entry.rect.x - (previous.rect.x + previous.rect.width) : 0
      if (!current || !previous || !sameLine || gap > maximumHeight * 1.5) runs.push([entry])
      else current.push(entry)
    }
    const candidates = runs.flatMap((run) => {
      const text = run.map((entry) => entry.item.text).join(' ')
      const value = parser(text)
      if (value === null) return []
      const rect = run.reduce((combined, entry) => combined ? unionRects(combined, entry.rect) : entry.rect, null as NormalizedRect | null)
      return rect ? [{ run, text, value, rect }] : []
    })
    if (candidates.length !== 1) return null
    const selected = candidates[0]!
    return {
      value: selected.value,
      text: selected.text,
      fullText: reading.text,
      confidence: Math.min(...selected.run.map((entry) => entry.item.confidence)),
      rect: expandAndClampTextRect(selected.rect, searchRect, metric),
    }
  }

  let groups: { items: typeof items; text: string; value: T; rect: NormalizedRect; confidence: number }[] = []
  for (let start = 0; start < items.length; start += 1) {
    for (let length = 1; length <= Math.min(6, items.length - start); length += 1) {
      const slice = items.slice(start, start + length)
      if (slice.length > 1 && !itemsShareLine(slice.map((entry) => entry.rect))) break
      const text = slice.map((entry) => entry.item.text).join(' ')
      const value = parser(text)
      if (value === null) continue
      const rect = slice.reduce((combined, entry) => combined ? unionRects(combined, entry.rect) : entry.rect, null as NormalizedRect | null)
      if (!rect) continue
      groups.push({
        items: slice,
        text,
        value,
        rect,
        confidence: Math.min(...slice.map((entry) => entry.item.confidence)),
      })
    }
  }

  if (!groups.length && fullValue !== null) {
    const rect = items.reduce((combined, entry) => combined ? unionRects(combined, entry.rect) : entry.rect, null as NormalizedRect | null)
    if (rect) groups.push({ items, text: reading.text, value: fullValue, rect, confidence: reading.confidence })
  }
  if (!groups.length) return null
  if (metric === 'level' && fullValue !== null) {
    groups = groups.filter((entry) => valueKey(entry.value) === valueKey(fullValue))
  }
  // A numeric prefix in another (even overlapping) box belongs to this EXP row.
  if (metric === 'exp') groups = groups.filter(group => !items.some(entry => !group.items.includes(entry)
    && /\d|EXP/i.test(entry.item.text.normalize('NFKC'))
    && itemsShareLine([...group.items.map(item => item.rect), entry.rect])))
  const highestQuality = Math.max(...groups.map((entry) => metricCandidateQuality(metric, entry.value)))
  groups = groups.filter((entry) => metricCandidateQuality(metric, entry.value) === highestQuality)
  if (!groups.length) return null
  const distinctValues = new Set(groups.map((entry) => valueKey(entry.value)))
  if (distinctValues.size !== 1) return null

  const selected = [...groups].sort((left, right) => rectArea(left.rect) - rectArea(right.rect))[0]
  if (!selected) return null
  const hasSeparateDuplicate = groups.some((entry) => entry !== selected
    && valueKey(entry.value) === valueKey(selected.value)
    && !rectContains(entry.rect, selected.rect)
    && rectIntersectionOverUnion(entry.rect, selected.rect) < 0.2)
  if (hasSeparateDuplicate) return null
  let rect = selected.rect
  if (selected.items.length === 1) {
    const span = metricTextSpan(metric, selected.items[0]!.item.text)
    if (span) rect = horizontalSubstringRect(rect, selected.items[0]!.item.text, span.index, span.length)
  }
  return {
    value: selected.value,
    text: selected.text,
    fullText: reading.text,
    confidence: selected.confidence,
    rect: expandAndClampTextRect(rect, searchRect, metric),
  }
}

export class AdaptiveRoiTracker {
  private readonly states = new Map<RecognitionMetricKey, MetricState>()

  private samplingIntervalMs = 0
  private metricIntervals: Partial<Record<RecognitionMetricKey, number>> = {}
  private metricCount = 1

  setSamplingCadence(intervalMs: number, intervals: Partial<Record<RecognitionMetricKey, number>>, metricCount: number): void {
    this.samplingIntervalMs = intervalMs
    this.metricIntervals = intervals
    this.metricCount = metricCount
  }

  private confirmationWindow(metric: RecognitionMetricKey): number {
    return Math.max(metric === 'jobName' ? JOB_TEXT_CONFIRMATION_WINDOW_MS : TEXT_CONFIRMATION_WINDOW_MS,
      3 * Math.max(this.samplingIntervalMs * this.metricCount, this.metricIntervals[metric] ?? 0))
  }

  resolve(
    metric: RecognitionMetricKey,
    searchRect: NormalizedRect,
    source: AdaptiveRoiSource,
    fallbackBarRect?: NormalizedRect,
  ): AdaptiveMetricRoi {
    const state = this.ensureState(metric, searchRect, source)
    const textMode = state.textRect
      ? 'adaptive'
      : state.textCandidates.length || state.legacyTextFailures > 0
        ? 'candidate'
        : 'legacy'
    const barMode = state.barRect
      ? 'adaptive'
      : state.barCandidates.length || state.legacyBarFailures > 0
        ? 'candidate'
        : 'legacy'
    const textRect = textMode === 'adaptive' ? state.textRect! : searchRect
    const barRect = barMode === 'adaptive' ? state.barRect! : fallbackBarRect
    const mode = textMode === 'adaptive' || barMode === 'adaptive'
      ? 'adaptive'
      : textMode === 'candidate' || barMode === 'candidate'
        ? 'candidate'
        : 'legacy'
    return {
      searchRect,
      textRect,
      ...(barRect ? { barRect } : {}),
      effectiveRect: barRect ? unionRects(textRect, barRect) : textRect,
      status: mode === 'adaptive'
        ? 'adapted'
        : mode === 'candidate'
          ? 'searching'
          : 'fallback',
      mode,
      textMode,
      barMode,
      source,
      confidence: state.confidence,
      updatedAt: state.updatedAt,
      ...(state.fallbackReason ? { fallbackReason: state.fallbackReason } : {}),
    }
  }

  observeText(metric: RecognitionMetricKey, searchRect: NormalizedRect, source: AdaptiveRoiSource, candidate: AdaptiveTextCandidate): boolean {
    const state = this.ensureState(metric, searchRect, source)
    if (state.textRect) return false
    if (state.legacyTextFailures === 0) return false
    state.textCandidates = state.textCandidates
      .filter((entry) => candidate.capturedAt >= entry.capturedAt
        && candidate.capturedAt - entry.capturedAt <= this.confirmationWindow(metric))
    if (!state.textCandidates.some((entry) => entry.capturedAt === candidate.capturedAt)) {
      state.textCandidates.push(candidate)
    }
    const valueMustRemainStable = metric === 'level' || metric === 'mapName' || metric === 'jobName'
    const previous = [...state.textCandidates]
      .reverse()
      .find((entry) => entry.capturedAt !== candidate.capturedAt
        && (!valueMustRemainStable || entry.key === candidate.key)
        && rectIntersectionOverUnion(entry.rect, candidate.rect) >= TEXT_CONFIRMATION_IOU)
    if (!previous || state.legacyTextFailures < LEGACY_FAILURE_LIMIT) return false
    state.textRect = medianRect([previous.rect, candidate.rect])
    state.textCandidates = []
    state.adaptiveTextFailures = 0
    state.confidence = Math.min(previous.confidence, candidate.confidence)
    state.updatedAt = candidate.capturedAt
    state.fallbackReason = null
    return true
  }

  observeBar(metric: RecognitionMetricKey, searchRect: NormalizedRect, source: AdaptiveRoiSource, candidate: AdaptiveBarCandidate): boolean {
    const state = this.ensureState(metric, searchRect, source)
    if (state.barRect) return false
    if (state.legacyBarFailures === 0) return false
    state.barCandidates = [...state.barCandidates.filter(entry => entry.capturedAt !== candidate.capturedAt), candidate]
      .filter((entry) => candidate.capturedAt >= entry.capturedAt
        && candidate.capturedAt - entry.capturedAt <= this.confirmationWindow(metric))
      .slice(-3)
    if (state.barCandidates.length < 3 || state.legacyBarFailures < LEGACY_FAILURE_LIMIT) return false
    const lefts = state.barCandidates.map((entry) => entry.rect.x)
    const rights = state.barCandidates.map((entry) => entry.rect.x + entry.rect.width)
    const tolerance = searchRect.width * BAR_EDGE_TOLERANCE
    if (Math.max(...lefts) - Math.min(...lefts) > tolerance
      || Math.max(...rights) - Math.min(...rights) > tolerance) {
      return false
    }
    state.barRect = medianRect(state.barCandidates.map((entry) => entry.rect))
    state.adaptiveBarFailures = 0
    state.confidence = Math.max(state.confidence, Math.min(...state.barCandidates.map((entry) => entry.confidence)))
    state.updatedAt = candidate.capturedAt
    state.barCandidates = []
    state.fallbackReason = null
    return true
  }

  markLegacySuccess(metric: RecognitionMetricKey, capturedAt: number, target: 'text' | 'bar' = 'text'): void {
    const state = this.states.get(metric)
    if (!state) return
    if (target === 'text') {
      const recovered = state.textRect !== null || state.textCandidates.length > 0 || state.legacyTextFailures > 0
      state.textRect = null
      state.textCandidates = []
      state.legacyTextFailures = 0
      state.adaptiveTextFailures = 0
      if (recovered) {
        state.fallbackReason = 'legacy-success'
        state.updatedAt = capturedAt
      }
    } else {
      const recovered = state.barRect !== null || state.barCandidates.length > 0 || state.legacyBarFailures > 0
      state.barRect = null
      state.barCandidates = []
      state.legacyBarFailures = 0
      state.adaptiveBarFailures = 0
      if (recovered) {
        state.fallbackReason = 'legacy-success'
        state.updatedAt = capturedAt
      }
    }
    state.failureStartedAt = null
  }

  markLegacyFailure(metric: RecognitionMetricKey, capturedAt: number, target: 'text' | 'bar' = 'text'): void {
    const state = this.states.get(metric)
    if (!state) return
    state.failureStartedAt ??= capturedAt
    if (target === 'text') state.legacyTextFailures += 1
    else state.legacyBarFailures += 1
  }

  markAdaptiveFailure(metric: RecognitionMetricKey, capturedAt: number, target: 'text' | 'bar' = 'text'): boolean {
    const state = this.states.get(metric)
    if (!state) return false
    state.failureStartedAt ??= capturedAt
    if (target === 'text') {
      state.adaptiveTextFailures += 1
      if (state.adaptiveTextFailures < ADAPTED_FAILURE_LIMIT) return false
      state.textRect = null
      state.textCandidates = []
      state.adaptiveTextFailures = 0
      state.legacyTextFailures = 0
    } else {
      state.adaptiveBarFailures += 1
      if (state.adaptiveBarFailures < ADAPTED_FAILURE_LIMIT) return false
      state.barRect = null
      state.barCandidates = []
      state.adaptiveBarFailures = 0
      state.legacyBarFailures = 0
    }
    state.fallbackReason = 'adaptive-failed'
    state.confidence = 0
    state.updatedAt = capturedAt
    return true
  }

  markAdaptiveSuccess(metric: RecognitionMetricKey, capturedAt: number, target: 'text' | 'bar' = 'text'): void {
    const state = this.states.get(metric)
    if (!state) return
    if (target === 'text') state.adaptiveTextFailures = 0
    else state.adaptiveBarFailures = 0
    state.failureStartedAt = null
    state.updatedAt = capturedAt
  }

  expire(metric: RecognitionMetricKey, capturedAt: number, maximumWaitMs: number): boolean {
    const state = this.states.get(metric)
    if (!state || state.failureStartedAt === null) return false
    if (capturedAt - state.failureStartedAt <= Math.max(maximumWaitMs, this.confirmationWindow(metric))) return false
    this.clearAdaptation(state)
    return true
  }

  reset(metric?: RecognitionMetricKey): void {
    if (metric) this.states.delete(metric)
    else this.states.clear()
  }

  private ensureState(metric: RecognitionMetricKey, searchRect: NormalizedRect, source: AdaptiveRoiSource): MetricState {
    const current = this.states.get(metric)
    if (current && current.source === source && rectEquals(current.searchRect, searchRect)) {
      current.searchRect = searchRect
      return current
    }
    const state: MetricState = {
      searchRect,
      source,
      textRect: null,
      barRect: null,
      textCandidates: [],
      barCandidates: [],
      legacyTextFailures: 0,
      legacyBarFailures: 0,
      adaptiveTextFailures: 0,
      adaptiveBarFailures: 0,
      failureStartedAt: null,
      confidence: 0,
      updatedAt: null,
      fallbackReason: null,
    }
    this.states.set(metric, state)
    return state
  }

  private clearAdaptation(state: MetricState): void {
    state.textRect = null
    state.barRect = null
    state.textCandidates = []
    state.barCandidates = []
    state.legacyTextFailures = 0
    state.legacyBarFailures = 0
    state.adaptiveTextFailures = 0
    state.adaptiveBarFailures = 0
    state.failureStartedAt = null
    state.confidence = 0
    state.updatedAt = null
    state.fallbackReason = null
  }
}

export function detectRatioBarRect(
  image: ImageData,
  searchRect: NormalizedRect,
  fallbackRect: NormalizedRect,
  targets: readonly (readonly [number, number, number])[],
  tolerance: number,
  ratio: number | null,
  capturedAt: number,
): AdaptiveBarCandidate | null {
  if (ratio === null || ratio <= 0 || ratio > 1) return null
  const search = toPixelRect(searchRect, image.width, image.height)
  const fallback = toPixelRect(fallbackRect, image.width, image.height)
  const yStart = Math.max(search.y, fallback.y - fallback.height)
  const yEnd = Math.min(search.y + search.height, fallback.y + fallback.height * 2)
  const rows: { first: number; last: number; y: number; matches: number }[] = []

  for (let y = yStart; y < yEnd; y += 1) {
    let first = -1
    let last = -1
    let matches = 0
    for (let x = search.x; x < search.x + search.width; x += 1) {
      const offset = (y * image.width + x) * 4
      const pixel = [image.data[offset] ?? 0, image.data[offset + 1] ?? 0, image.data[offset + 2] ?? 0]
      if (!targets.some((target) => colorDistance(pixel, target) <= tolerance)) continue
      if (first < 0) first = x
      last = x
      matches += 1
    }
    if (first >= 0 && matches >= Math.max(2, fallback.width * ratio * 0.2)) rows.push({ first, last, y, matches })
  }
  if (rows.length < 2) return null

  const first = median(rows.map((entry) => entry.first))
  const last = median(rows.map((entry) => entry.last))
  const centerY = median(rows.map((entry) => entry.y))
  if (first === null || last === null || centerY === null || last < first) return null
  const top = Math.min(...rows.map((entry) => entry.y))
  const bottom = Math.max(...rows.map((entry) => entry.y))
  const height = Math.max(2, bottom - top + 1)
  const minimumWidth = Math.max(8, Math.round(fallback.width * 0.6))
  const maximumWidth = Math.min(search.width, Math.round(fallback.width * 1.5))
  const minimumLeft = Math.max(search.x, first - Math.round(maximumWidth * 0.12))
  const maximumRight = Math.min(search.x + search.width, first + maximumWidth)
  let best: { left: number; right: number; confidence: number } | null = null
  for (let left = minimumLeft; left <= first; left += 1) {
    const leftConfidence = verticalBoundaryConfidence(image, left, top, bottom)
    if (leftConfidence < 0.35) continue
    for (let right = Math.max(last + 1, left + minimumWidth); right <= maximumRight; right += 1) {
      const width = right - left
      if (width > maximumWidth) break
      const observedRatio = Math.min(1, (last - left + 1) / width)
      const ratioError = Math.abs(observedRatio - ratio)
      if (ratioError > 0.035) continue
      const rightConfidence = verticalBoundaryConfidence(image, right, top, bottom)
      const edgeConfidence = Math.min(leftConfidence, rightConfidence)
      if (edgeConfidence < 0.35) continue
      const widthSimilarity = 1 - Math.min(1, Math.abs(width - fallback.width) / Math.max(1, fallback.width))
      const confidence = edgeConfidence * 0.7 + widthSimilarity * 0.3
      if (!best || confidence > best.confidence) best = { left, right, confidence }
    }
  }
  if (!best) return null
  const rect = clampRect({
    x: best.left / image.width,
    y: top / image.height,
    width: (best.right - best.left) / image.width,
    height: height / image.height,
  })
  return {
    rect,
    confidence: Math.min(best.confidence, Math.min(1, rows.length / Math.max(2, fallback.height))),
    capturedAt,
  }
}

export function expandAndClampTextRect(
  rect: NormalizedRect,
  searchRect: NormalizedRect,
  metric: RecognitionMetricKey,
): NormalizedRect {
  const height = rect.height
  const horizontal = metric === 'level' ? height * 0.35 : height * 0.22
  const extraRight = metric === 'level' ? height * 0.75 : horizontal
  const vertical = height * 0.22
  let width = rect.width + horizontal + extraRight
  let x = rect.x - horizontal
  if (metric === 'level' && width < height * 4.5) {
    width = height * 4.5
  }
  return intersectRect(clampRect({
    x,
    y: rect.y - vertical,
    width,
    height: rect.height + vertical * 2,
  }), searchRect)
}

export function rectIntersectionOverUnion(left: NormalizedRect, right: NormalizedRect): number {
  const x1 = Math.max(left.x, right.x)
  const y1 = Math.max(left.y, right.y)
  const x2 = Math.min(left.x + left.width, right.x + right.width)
  const y2 = Math.min(left.y + left.height, right.y + right.height)
  const intersection = Math.max(0, x2 - x1) * Math.max(0, y2 - y1)
  const union = left.width * left.height + right.width * right.height - intersection
  return union > 0 ? intersection / union : 0
}

function medianRect(rects: readonly NormalizedRect[]): NormalizedRect {
  const x = median(rects.map((rect) => rect.x)) ?? rects[0]?.x ?? 0
  const y = median(rects.map((rect) => rect.y)) ?? rects[0]?.y ?? 0
  const right = median(rects.map((rect) => rect.x + rect.width)) ?? x + (rects[0]?.width ?? 0.002)
  const bottom = median(rects.map((rect) => rect.y + rect.height)) ?? y + (rects[0]?.height ?? 0.002)
  return clampRect({ x, y, width: right - x, height: bottom - y })
}

function unionRects(left: NormalizedRect, right: NormalizedRect): NormalizedRect {
  const x = Math.min(left.x, right.x)
  const y = Math.min(left.y, right.y)
  return clampRect({
    x,
    y,
    width: Math.max(left.x + left.width, right.x + right.width) - x,
    height: Math.max(left.y + left.height, right.y + right.height) - y,
  })
}

function intersectRect(rect: NormalizedRect, bounds: NormalizedRect): NormalizedRect {
  const x = Math.max(rect.x, bounds.x)
  const y = Math.max(rect.y, bounds.y)
  const right = Math.min(rect.x + rect.width, bounds.x + bounds.width)
  const bottom = Math.min(rect.y + rect.height, bounds.y + bounds.height)
  if (right <= x || bottom <= y) return bounds
  return clampRect({ x, y, width: right - x, height: bottom - y })
}

function colorDistance(a: readonly number[], b: readonly number[]): number {
  return Math.sqrt((a[0]! - b[0]!) ** 2 + (a[1]! - b[1]!) ** 2 + (a[2]! - b[2]!) ** 2)
}

function itemsShareLine(rects: readonly NormalizedRect[]): boolean {
  const centers = rects.map((rect) => rect.y + rect.height / 2)
  const maximumHeight = Math.max(...rects.map((rect) => rect.height))
  return Math.max(...centers) - Math.min(...centers) <= maximumHeight * 0.75
}

function valueKey(value: unknown): string {
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

function metricCandidateQuality(metric: RecognitionMetricKey, value: unknown): number {
  if (typeof value !== 'object' || value === null) return 1
  if (metric === 'exp' && 'absolute' in value && 'ratio' in value) {
    return Number(value.absolute !== null) + Number(value.ratio !== null)
  }
  if ((metric === 'hp' || metric === 'mp') && 'current' in value && 'max' in value) {
    return Number(value.current !== null) + Number(value.max !== null)
  }
  return 1
}

function metricTextSpan(metric: RecognitionMetricKey, text: string): { index: number; length: number } | null {
  const normalized = text.normalize('NFKC')
  const pattern = metric === 'level'
    ? /L\s*V\s*\.?\s*(?:[\dOoIl|]\s*){1,3}/i
    : metric === 'exp'
      ? /EXP[\s\S]*?[%％]/i
      : metric === 'hp' || metric === 'mp'
        ? /[\dOoIl|,]+\s*\/\s*[\dOoIl|,]+/
        : metric === 'mesos'
          ? /[\dOoIl|](?:[\dOoIl|,.\s]*[\dOoIl|])?/
          : null
  const match = pattern?.exec(normalized)
  return match?.index === undefined ? null : { index: match.index, length: match[0].length }
}

function horizontalSubstringRect(rect: NormalizedRect, text: string, index: number, length: number): NormalizedRect {
  const characters = [...text]
  const before = characters.slice(0, index).reduce((sum, character) => sum + characterWeight(character), 0)
  const selected = characters.slice(index, index + length).reduce((sum, character) => sum + characterWeight(character), 0)
  const total = Math.max(1, characters.reduce((sum, character) => sum + characterWeight(character), 0))
  return clampRect({
    x: rect.x + rect.width * before / total,
    y: rect.y,
    width: rect.width * selected / total,
    height: rect.height,
  })
}

function characterWeight(character: string): number {
  if (/\s/.test(character)) return 0.35
  if (/[^\x00-\xff]/.test(character)) return 1
  if (/[.,:;\[\]()]/.test(character)) return 0.35
  return 0.62
}

function rectArea(rect: NormalizedRect): number {
  return rect.width * rect.height
}

function rectContains(outer: NormalizedRect, inner: NormalizedRect): boolean {
  const epsilon = 1e-5
  return outer.x <= inner.x + epsilon
    && outer.y <= inner.y + epsilon
    && outer.x + outer.width >= inner.x + inner.width - epsilon
    && outer.y + outer.height >= inner.y + inner.height - epsilon
}

function rectEquals(left: NormalizedRect, right: NormalizedRect): boolean {
  const epsilon = 1e-6
  return Math.abs(left.x - right.x) <= epsilon
    && Math.abs(left.y - right.y) <= epsilon
    && Math.abs(left.width - right.width) <= epsilon
    && Math.abs(left.height - right.height) <= epsilon
}

function verticalBoundaryConfidence(image: ImageData, expectedX: number, startY: number, endY: number): number {
  const minimumY = Math.max(0, Math.min(image.height - 1, startY))
  const maximumY = Math.max(minimumY, Math.min(image.height - 1, endY))
  let best = 0
  for (let x = Math.max(1, expectedX - 2); x <= Math.min(image.width - 2, expectedX + 2); x += 1) {
    let strong = 0
    let samples = 0
    for (let y = minimumY; y <= maximumY; y += 1) {
      const leftOffset = (y * image.width + x - 1) * 4
      const rightOffset = (y * image.width + x + 1) * 4
      const distance = colorDistance(
        [image.data[leftOffset] ?? 0, image.data[leftOffset + 1] ?? 0, image.data[leftOffset + 2] ?? 0],
        [image.data[rightOffset] ?? 0, image.data[rightOffset + 1] ?? 0, image.data[rightOffset + 2] ?? 0],
      )
      if (distance >= 28) strong += 1
      samples += 1
    }
    if (samples) best = Math.max(best, strong / samples)
  }
  return best
}
